import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'

import { fromDateRangePickerValue, toDateRangePickerValue } from '@shared/lib/routes'

import { useTutorAttendanceRatesStore } from './store'
import { useTutorAttendanceRateOptions } from './tutor-attendance-rate-options'
import {
  isBookingStatus,
  isTutorAttendanceRateReason,
  tutorAttendanceRatesFiltersToQuery,
} from '../lib/tutor-attendance-rates-query'

interface FiltersTutorAttendanceRatesReturn {
  refDateRange: WritableComputedRef<Date[] | null>
  refTutorIds: WritableComputedRef<number[]>
  refStatuses: WritableComputedRef<string[]>
  refReasons: WritableComputedRef<string[]>
  statusOptions: ComputedRef<AppSelectOption[]>
  reasonOptions: ComputedRef<AppSelectOption[]>
  isLoading: ComputedRef<boolean>
  clearFilters: () => void
}

export const useFiltersTutorAttendanceRates = (): FiltersTutorAttendanceRatesReturn => {
  const router = useRouter()
  const route = useRoute()
  const store = useTutorAttendanceRatesStore()
  const { statusOptions, reasonOptions } = useTutorAttendanceRateOptions()

  /**
   * The one place a filter change reaches the URL: write the store, push the query (the page refetches on query change).
   * `page` is dropped so a new filter never lands on a page that no longer exists.
   */
  const syncQuery = async (): Promise<void> => {
    await router.push({
      query: {
        ...route.query,
        ...tutorAttendanceRatesFiltersToQuery(store.getFilters),
        page: undefined,
      },
    })
  }

  const refDateRange = computed<Date[] | null>({
    get: () => toDateRangePickerValue(store.getFilters),
    set: (value) => {
      store.setFilterDates(fromDateRangePickerValue(value))
      void syncQuery()
    },
  })

  const refTutorIds = computed<number[]>({
    get: () => store.getFilters.tutorIds,
    set: (value) => {
      store.setFilterTutorIds(value)
      void syncQuery()
    },
  })

  const refStatuses = computed<string[]>({
    get: () => store.getFilters.statuses,
    set: (value) => {
      store.setFilterStatuses(value.filter(isBookingStatus))
      void syncQuery()
    },
  })

  const refReasons = computed<string[]>({
    get: () => store.getFilters.reasons,
    set: (value) => {
      store.setFilterReasons(value.filter(isTutorAttendanceRateReason))
      void syncQuery()
    },
  })

  const isLoading = computed<boolean>(() => store.isLoading)

  const clearFilters = (): void => {
    store.resetFilters()
    void syncQuery()
  }

  return {
    refDateRange,
    refTutorIds,
    refStatuses,
    refReasons,
    statusOptions,
    reasonOptions,
    isLoading,
    clearFilters,
  }
}
